import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Modal, message } from "antd";
import Button from "../../../reusable/Button/Button";
import Input from "../../../reusable/Input/Input";
import Select from "../../../reusable/Select/Select";
import TextArea from "../../../reusable/TextArea/TextArea";
import {
  useGetPayoutAccountsQuery,
  useGetTotalCommissionQuery,
  useRequestPayoutMutation,
} from "../../../store/api/payoutsApi";
import { usd } from "../../../utilities/format";
import { t } from "../../../i18n/i18n";
import "./RequestPayout.scss";

/**
 * The tab that sent us here, and what `type` that is in the request body. The
 * keys are the `?type=` the Payout page's own tabs carry over, so the affiliate
 * tab opens the affiliate form rather than falling through to the trading one.
 */
const KINDS = {
  profit_share: { api: "Profit Share", title: "Request trading payout" },
  affiliate_share: { api: "Affiliate Share", title: "Request affiliate payout" },
};

/** Each side has a floor; the backend enforces it, the form says so first. */
const MIN_COMMISSION = 100;
const MIN_TRADING = 80;

/**
 * `/v2/get-payout-accounts/` answers `{ accounts: [id], data: [{ account,
 * payable_amt }] }` — the same accounts twice. `data` is the one worth reading:
 * it carries what each account may draw, which caps the amount below.
 */
const accountsOf = (payload) => (Array.isArray(payload?.data) ? payload.data : []);

const commissionOf = (data) =>
  data?.total_commission ?? data?.available_commission ?? data?.amount ?? data?.total ?? 0;

export default function RequestPayout() {
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const kind = KINDS[search.get("type")] ? search.get("type") : "profit_share";
  const affiliate = kind === "affiliate_share";

  const [form, setForm] = useState({ login_id: "", amount: "", reason: "" });
  const [touched, setTouched] = useState(false);

  const set = (key) => (e) =>
    setForm((prev) => ({ ...prev, [key]: e?.target ? e.target.value : e }));

  const accounts = useGetPayoutAccountsQuery(undefined, { skip: affiliate });
  const commission = useGetTotalCommissionQuery(undefined, { skip: !affiliate });
  const [submit, { isLoading }] = useRequestPayoutMutation();

  const rows = useMemo(() => accountsOf(accounts.data), [accounts.data]);

  const options = useMemo(
    () => rows.map((row) => ({ label: String(row.account), value: String(row.account) })),
    [rows],
  );

  /** What the chosen account may draw — the cap the amount is checked against. */
  const payable = Number(
    rows.find((row) => String(row.account) === form.login_id)?.payable_amt,
  );

  const available = affiliate
    ? Number(commissionOf(commission.data)) || 0
    : (Number.isFinite(payable) ? payable : 0);

  const amount = Number(form.amount);
  const minimum = affiliate ? MIN_COMMISSION : MIN_TRADING;

  /** Each field names its own problem, so a missing account doesn't read as an amount error. */
  const loginError = !affiliate && !form.login_id ? t("Account Login ID is required") : null;

  const amountError = (() => {
    if (!form.amount.trim()) return t("Amount is required");
    if (!Number.isFinite(amount) || amount <= 0) return t("Enter a valid amount");
    if (amount < minimum)
      return t("Amount must be at least {{amount}}", { amount: usd(minimum, { decimals: 0 }) });
    // Trading has nothing to cap against until an account is chosen.
    if ((affiliate || form.login_id) && amount > available)
      return t("Amount cannot exceed the {{amount}} available", {
        amount: usd(available, { decimals: 2 }),
      });
    return null;
  })();

  const invalid = Boolean(loginError || amountError);

  /** Submit only validates; the request goes out once the trader clicks Ok. */
  const [confirming, setConfirming] = useState(false);

  const review = (e) => {
    e.preventDefault();
    setTouched(true);
    if (!invalid) setConfirming(true);
  };

  const send = async () => {
    try {
      await submit({
        amount: form.amount,
        reason: form.reason,
        type: KINDS[kind].api,
        ...(affiliate ? {} : { login_id: form.login_id }),
      }).unwrap();
      setConfirming(false);
      message.success(t("Payout request submitted"));
      navigate("/payout");
    } catch (err) {
      setConfirming(false);
      message.error(err?.data?.message ?? t("Could not submit your payout request"));
    }
  };

  return (
    <form className="request-card" onSubmit={review} noValidate>
      <div className="request-card__head">
        <span className="request-card__title">{t(KINDS[kind].title)}</span>
        <p className="request-card__desc">
          {t("Please note*:")}{" "}
          {affiliate ? (
            <span className="request-card__desc-warn">
              {t("Minimum available commission to withdraw is {{amount}}", {
                amount: usd(MIN_COMMISSION, { decimals: 0 }),
              })}
            </span>
          ) : (
            t("Accounts in the below dropdown will populate only on their respective withdrawal dates.")
          )}
        </p>
      </div>

      <div className="request-card__fields">
        {affiliate ? (
          <>
            <Input
              type="number"
              label="Amount Withdraw"
              placeholder="Enter amount"
              min={MIN_COMMISSION}
              value={form.amount}
              onChange={set("amount")}
              error={touched ? amountError : null}
            />
            {/* A figure, not a field — the same control, read-only, so the row pairs up. */}
            <Input
              label="Available Amount"
              value={commission.isLoading ? "…" : usd(available, { decimals: 0 })}
              readOnly
              onChange={() => {}}
            />
          </>
        ) : (
          <>
            <Select
              variant="form"
              className="request-card__select"
              required
              label="Choose Account Login ID"
              placeholder={
                accounts.isLoading
                  ? "Loading…"
                  : options.length
                    ? "Select an account"
                    : "No accounts available today"
              }
              options={options}
              value={form.login_id}
              onChange={set("login_id")}
              disabled={!options.length}
              error={touched ? loginError : null}
            />
            <Input
              type="number"
              label="Withdraw Amount"
              placeholder="Enter amount"
              max={form.login_id ? available : undefined}
              hint={
                form.login_id
                  ? t("{{amount}} available on this account", {
                    amount: usd(available, { decimals: 2 }),
                  })
                  : null
              }
              value={form.amount}
              onChange={set("amount")}
              error={touched ? amountError : null}
            />
          </>
        )}

        <div className="request-card__wide">
          <TextArea
            label="Reason"
            placeholder="Add a note for the payouts team (optional)"
            rows={5}
            value={form.reason}
            onChange={set("reason")}
          />
        </div>
      </div>

      <span className="request-card__rule" />

      <div className="request-card__actions">
        <Button variant="ghost" disabled={isLoading} onClick={() => navigate("/payout")}>
          {t("Cancel")}
        </Button>
        <Button variant="submit" htmlType="submit" disabled={isLoading}>
          {isLoading ? t("Submitting…") : t("Submit")}
        </Button>
      </div>

      <Modal
        open={confirming}
        onCancel={isLoading ? undefined : () => setConfirming(false)}
        footer={null}
        closable={false}
        mask={{ closable: !isLoading }}
        centered
        width={360}
        destroyOnHidden
        rootClassName="request-confirm-root"
        aria-label={t("Confirm payout request")}
      >
        <p className="request-confirm__text">
          {affiliate
            ? t("Please note: Your affiliate payout request of {{amount}} will be sent for review. Click Ok to continue.", {
              amount: usd(amount, { decimals: 2 }),
            })
            : t("Please note: All open trades on account {{login}} will be closed, if any, and the account will be disabled until your request is reviewed. Click Ok to continue.", {
              login: form.login_id,
            })}
        </p>
        <div className="request-confirm__actions">
          <Button variant="submit" height={32} loading={isLoading} onClick={send}>
            {t("Ok")}
          </Button>
          <Button
            variant="ghost"
            height={32}
            className="request-confirm__cancel"
            disabled={isLoading}
            onClick={() => setConfirming(false)}
          >
            {t("Cancel")}
          </Button>
        </div>
      </Modal>
    </form>
  );
}
